/**
 * Meetings Service - API calls for meetings management
 * Module Rencontres - Gestion des rendez-vous entre utilisateurs
 */

const API_BASE = `${import.meta.env.VITE_API_BASE_URL}/meetings`;
const FILES_BASE_URL = import.meta.env.VITE_FILES_BASE_URL || 'https://www.cabaza.be/files';

/**
 * En-têtes d'un appel qui écrit.
 *
 * Le serveur vérifie désormais que la rencontre ou l'invitation visée
 * appartient bien à l'appelant. Il lui faut donc une identité prouvée : le
 * JWT signé au login, et non le `creatorId`/`peopleId`/`userId` que le corps
 * de la requête déclarait jusqu'ici — ce dernier venait du client, et se
 * remplaçait.
 *
 * Le jeton est écrit sous `authToken` par `AuthContext.saveUserToStorage` ;
 * `sessionStorage` prend le relais quand l'utilisateur n'a pas coché « rester
 * connecté ».
 */
function enTetesEcriture(avecCorps = true) {
    let token = null;
    try {
        token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    } catch {
        token = null;
    }

    const headers = { 'Accept': 'application/json' };
    if (avecCorps) headers['Content-Type'] = 'application/json';
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
}

/**
 * Meeting statuses with colors
 */
export const MEETING_STATUSES = {
    pending: { color: '#f59e0b', icon: 'Clock' },
    confirmed: { color: '#22c55e', icon: 'CheckCircle' },
    cancelled: { color: '#ef4444', icon: 'XCircle' },
    locked: { color: '#3b82f6', icon: 'Lock' },
    proposal: { color: '#8b5cf6', icon: 'MessageCircle' }
};

/**
 * Meeting types with colors
 */
export const MEETING_TYPES = {
    in_person: { color: '#3b82f6', icon: 'MapPin' },
    video: { color: '#8b5cf6', icon: 'Video' },
    phone: { color: '#22c55e', icon: 'Phone' },
    other: { color: '#6b7280', icon: 'Calendar' }
};

/**
 * Module color
 */
export const MODULE_COLOR = '#e11d48';

/**
 * Get all meetings for a user
 * @param {number} peopleId - People ID
 * @param {string} status - Status filter: 'pending', 'confirmed', 'cancelled', 'completed', '' (all)
 * @returns {Promise<Array>} List of meetings
 */
export const getMeetings = async (peopleId, status = '') => {
    try {
        const params = new URLSearchParams({ peopleId });
        if (status) params.append('status', status);
        const response = await fetch(`${API_BASE}?${params}`, {
            headers: { 'Accept': 'application/json' }
        });
        if (!response.ok) throw new Error('Failed to fetch meetings');
        const data = await response.json();
        const success = data.success ?? data.SUCCESS;
        const meetings = data.data ?? data.DATA ?? [];
        return success ? meetings : [];
    } catch (error) {
        console.error('Error fetching meetings:', error);
        return [];
    }
};

/**
 * Get single meeting by ID
 * @param {number} meetingId - Meeting ID
 * @param {number} peopleId - People ID (for access check)
 * @returns {Promise<Object|null>} Meeting data or null
 */
export const getMeetingById = async (meetingId, peopleId) => {
    try {
        const response = await fetch(`${API_BASE}/${meetingId}?peopleId=${peopleId}`, {
            headers: { 'Accept': 'application/json' }
        });
        if (!response.ok) throw new Error('Failed to fetch meeting');
        const data = await response.json();
        const success = data.success ?? data.SUCCESS;
        const meeting = data.data ?? data.DATA ?? null;
        return success ? meeting : null;
    } catch (error) {
        console.error('Error fetching meeting:', error);
        return null;
    }
};

/**
 * Create a new meeting
 * @param {Object} meetingData - Meeting data
 * @returns {Promise<Object>} Result with success status
 */
export const createMeeting = async (meetingData) => {
    try {
        const response = await fetch(`${API_BASE}`, {
            method: 'POST',
            headers: enTetesEcriture(),
            body: JSON.stringify(meetingData)
        });
        const result = await response.json();
        return {
            success: result.success ?? result.SUCCESS,
            message: result.message ?? result.MESSAGE,
            meetingId: result.meetingId ?? result.MEETINGID ?? result.data?.meetingId ?? result.DATA?.MEETINGID,
            denyCode: result.denyCode ?? result.DENYCODE ?? result.denycode,
            current: result.current ?? result.CURRENT,
            max: result.max ?? result.MAX
        };
    } catch (error) {
        console.error('Error creating meeting:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Update a meeting
 * @param {number} meetingId - Meeting ID
 * @param {Object} meetingData - Update data
 * @returns {Promise<Object>} Result with success status
 */
export const updateMeeting = async (meetingId, meetingData) => {
    try {
        const response = await fetch(`${API_BASE}/${meetingId}`, {
            method: 'PUT',
            headers: enTetesEcriture(),
            body: JSON.stringify(meetingData)
        });
        const result = await response.json();

        // Debug logs from CFC
        const debugLogs = result.debugLogs ?? result.DEBUGLOGS ?? [];
        if (debugLogs.length > 0) {
            console.group('🔔 Meeting Update - Debug Logs');
            debugLogs.forEach(log => console.log(log));
            console.groupEnd();
        }

        return {
            success: result.success ?? result.SUCCESS,
            message: result.message ?? result.MESSAGE
        };
    } catch (error) {
        console.error('Error updating meeting:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Delete a meeting
 * @param {number} meetingId - Meeting ID
 * @param {number} userId - User ID (for ownership verification)
 * @returns {Promise<Object>} Result with success status
 */
export const deleteMeeting = async (meetingId, userId) => {
    try {
        const response = await fetch(`${API_BASE}/${meetingId}?userId=${userId}`, {
            method: 'DELETE',
            headers: enTetesEcriture(false)
        });
        const result = await response.json();
        return {
            success: result.success ?? result.SUCCESS,
            message: result.message ?? result.MESSAGE
        };
    } catch (error) {
        console.error('Error deleting meeting:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Respond to a meeting invitation
 * @param {number} meetingId - Meeting ID
 * @param {number} participantId - Participant (people) ID
 * @param {string} responseType - Response: 'ACCEPTED', 'DECLINED', 'PROPOSED'
 * @returns {Promise<Object>} Result with success status
 */
export const respondToMeeting = async (meetingId, participantId, responseType) => {
    try {
        const response = await fetch(`${API_BASE}/${meetingId}/participants/${participantId}`, {
            method: 'PUT',
            headers: enTetesEcriture(),
            body: JSON.stringify({ response: responseType.toUpperCase() })
        });
        const result = await response.json();
        return {
            success: result.success ?? result.SUCCESS,
            message: result.message ?? result.MESSAGE
        };
    } catch (error) {
        console.error('Error responding to meeting:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Close a meeting (only organizer can do this)
 * Once closed, no more proposals can be made
 * @param {number} meetingId - Meeting ID
 * @param {number} creatorId - Creator/Organizer ID
 * @param {string} closureMessage - Optional closure message (thanks, links, etc.)
 * @returns {Promise<Object>} Result with success status
 */
export const lockMeeting = async (meetingId, creatorId, lockMessage = '') => {
    try {
        const response = await fetch(`${API_BASE}/${meetingId}/lock`, {
            method: 'POST',
            headers: enTetesEcriture(),
            body: JSON.stringify({ creatorId, lockMessage })
        });
        const result = await response.json();
        return {
            success: result.success ?? result.SUCCESS,
            message: result.message ?? result.MESSAGE
        };
    } catch (error) {
        console.error('Error locking meeting:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Unlock (reopen) a meeting
 * @param {number} meetingId - Meeting ID
 * @param {number} creatorId - Creator ID
 * @returns {Promise<Object>} Result with success status
 */
export const unlockMeeting = async (meetingId, creatorId) => {
    try {
        const response = await fetch(`${API_BASE}/${meetingId}/unlock`, {
            method: 'POST',
            headers: enTetesEcriture(),
            body: JSON.stringify({ creatorId })
        });
        const result = await response.json();
        return {
            success: result.success ?? result.SUCCESS,
            message: result.message ?? result.MESSAGE
        };
    } catch (error) {
        console.error('Error unlocking meeting:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Cancel a meeting (without deleting it)
 * @param {number} meetingId - Meeting ID
 * @param {number} creatorId - Creator ID
 * @param {string} reason - Cancellation reason
 * @returns {Promise<Object>} Result with success status
 */
export const cancelMeeting = async (meetingId, creatorId, reason = '') => {
    try {
        const response = await fetch(`${API_BASE}/${meetingId}?reason=${encodeURIComponent(reason)}`, {
            method: 'DELETE',
            headers: enTetesEcriture(false)
        });
        const result = await response.json();
        return {
            success: result.success ?? result.SUCCESS,
            message: result.message ?? result.MESSAGE
        };
    } catch (error) {
        console.error('Error cancelling meeting:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Check if a meeting is closed
 * @param {Object} meeting - Meeting object
 * @returns {boolean} True if closed
 */
export const isMeetingClosed = (meeting) => {
    if (!meeting) return false;
    const status = (meeting.status || meeting.STATUS || '').toUpperCase();
    return status === 'LOCKED';
};

/**
 * Propose a modification to a meeting
 * Uses the existing respondToMeeting endpoint with response=PROPOSED
 * @param {number} meetingId - Meeting ID
 * @param {number} userId - User ID (participant)
 * @param {Object} proposal - Proposed changes (proposedDate, proposedEndDate, proposedLocation, message)
 * @returns {Promise<Object>} Result with success status
 */
export const proposeMeetingChange = async (meetingId, userId, proposal) => {
    try {
        const response = await fetch(`${API_BASE}/${meetingId}/participants/${userId}`, {
            method: 'PUT',
            headers: enTetesEcriture(),
            body: JSON.stringify({
                response: 'PROPOSED',
                proposedDate: proposal.proposedDate || null,
                proposedEndDate: proposal.proposedEndDate || null,
                proposedPlaceName: proposal.proposedLocation || null,
                proposedMessage: proposal.message || null
            })
        });
        const result = await response.json();
        return {
            success: result.success ?? result.SUCCESS,
            message: result.message ?? result.MESSAGE
        };
    } catch (error) {
        console.error('Error proposing meeting change:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Accept a proposal from a participant (organizer only)
 * @param {number} meetingId - Meeting ID
 * @param {number} participantId - Participant ID who made the proposal
 * @param {number} creatorId - Creator/organizer ID
 * @returns {Promise<Object>} Result with success status
 */
export const acceptProposal = async (meetingId, participantId, creatorId) => {
    try {
        const response = await fetch(`${API_BASE}/${meetingId}/proposals/${participantId}/accept`, {
            method: 'POST',
            headers: enTetesEcriture(),
            body: JSON.stringify({ creatorId })
        });
        const result = await response.json();
        return {
            success: result.success ?? result.SUCCESS,
            message: result.message ?? result.MESSAGE
        };
    } catch (error) {
        console.error('Error accepting proposal:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Decline a proposal from a participant (organizer only)
 * @param {number} meetingId - Meeting ID
 * @param {number} participantId - Participant ID who made the proposal
 * @param {number} creatorId - Creator/organizer ID
 * @returns {Promise<Object>} Result with success status
 */
export const declineProposal = async (meetingId, participantId, creatorId) => {
    try {
        const response = await fetch(`${API_BASE}/${meetingId}/proposals/${participantId}/decline`, {
            method: 'POST',
            headers: enTetesEcriture(),
            body: JSON.stringify({ creatorId })
        });
        const result = await response.json();
        return {
            success: result.success ?? result.SUCCESS,
            message: result.message ?? result.MESSAGE
        };
    } catch (error) {
        console.error('Error declining proposal:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Save meeting to personal agenda
 * @param {number} meetingId - Meeting ID
 * @param {number} peopleId - People ID
 * @param {string} agendaEventType - Type: 'meeting' or 'appointment'
 * @returns {Promise<Object>} Result with success status and agendaId
 */
export const saveMeetingToAgenda = async (meetingId, peopleId, agendaEventType) => {
    try {
        const response = await fetch(`${API_BASE}/${meetingId}/save-to-agenda`, {
            method: 'POST',
            headers: enTetesEcriture(),
            body: JSON.stringify({ peopleId, agendaEventType })
        });
        const result = await response.json();
        return {
            success: result.success ?? result.SUCCESS,
            message: result.message ?? result.MESSAGE,
            agendaId: result.agendaId ?? result.AGENDAID ?? 0
        };
    } catch (error) {
        console.error('Error saving meeting to agenda:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Get meeting participants
 * @param {number} meetingId - Meeting ID
 * @returns {Promise<Array>} List of participants
 */
export const getMeetingParticipants = async (meetingId) => {
    try {
        const response = await fetch(`${API_BASE}/${meetingId}/participants`, {
            headers: { 'Accept': 'application/json' }
        });
        if (!response.ok) throw new Error('Failed to fetch participants');
        const data = await response.json();
        const success = data.success ?? data.SUCCESS;
        const participants = data.data ?? data.DATA ?? [];
        return success ? participants : [];
    } catch (error) {
        console.error('Error fetching meeting participants:', error);
        return [];
    }
};

/**
 * Add participant to meeting
 * @param {number} meetingId - Meeting ID
 * @param {number} peopleId - People ID to add
 * @param {number} userId - User ID (organizer)
 * @returns {Promise<Object>} Result with success status
 */
export const addMeetingParticipant = async (meetingId, peopleId, userId) => {
    try {
        const response = await fetch(`${API_BASE}/${meetingId}/participants`, {
            method: 'POST',
            headers: enTetesEcriture(),
            body: JSON.stringify({ peopleId, userId })
        });
        const result = await response.json();
        return {
            success: result.success ?? result.SUCCESS,
            message: result.message ?? result.MESSAGE
        };
    } catch (error) {
        console.error('Error adding participant:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Remove participant from meeting
 * @param {number} meetingId - Meeting ID
 * @param {number} peopleId - People ID to remove
 * @param {number} userId - User ID (organizer)
 * @returns {Promise<Object>} Result with success status
 */
export const removeMeetingParticipant = async (meetingId, peopleId, userId) => {
    try {
        const response = await fetch(`${API_BASE}/${meetingId}/participants/${peopleId}?userId=${userId}`, {
            method: 'DELETE',
            headers: enTetesEcriture(false)
        });
        const result = await response.json();
        return {
            success: result.success ?? result.SUCCESS,
            message: result.message ?? result.MESSAGE
        };
    } catch (error) {
        console.error('Error removing participant:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Get meeting comments
 * @param {number} meetingId - Meeting ID
 * @returns {Promise<Array>} List of comments
 */
export const getMeetingComments = async (meetingId) => {
    try {
        const response = await fetch(`${API_BASE}/${meetingId}/comments`, {
            headers: { 'Accept': 'application/json' }
        });
        if (!response.ok) throw new Error('Failed to fetch comments');
        const data = await response.json();
        const success = data.success ?? data.SUCCESS;
        const comments = data.data ?? data.DATA ?? [];
        return success ? comments : [];
    } catch (error) {
        console.error('Error fetching meeting comments:', error);
        return [];
    }
};

/**
 * Add comment to meeting
 * @param {number} meetingId - Meeting ID
 * @param {number} userId - User ID
 * @param {string} content - Comment content
 * @returns {Promise<Object>} Result with success status
 */
export const addMeetingComment = async (meetingId, userId, content) => {
    try {
        const response = await fetch(`${API_BASE}/${meetingId}/comments`, {
            method: 'POST',
            headers: enTetesEcriture(),
            body: JSON.stringify({ userId, content })
        });
        const result = await response.json();
        return {
            success: result.success ?? result.SUCCESS,
            message: result.message ?? result.MESSAGE,
            commentId: result.commentId ?? result.COMMENTID
        };
    } catch (error) {
        console.error('Error adding comment:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Delete comment from meeting
 * @param {number} meetingId - Meeting ID
 * @param {number} commentId - Comment ID
 * @param {number} userId - User ID
 * @returns {Promise<Object>} Result with success status
 */
export const deleteMeetingComment = async (meetingId, commentId, userId) => {
    try {
        const response = await fetch(`${API_BASE}/${meetingId}/comments/${commentId}?userId=${userId}`, {
            method: 'DELETE',
            headers: enTetesEcriture(false)
        });
        const result = await response.json();
        return {
            success: result.success ?? result.SUCCESS,
            message: result.message ?? result.MESSAGE
        };
    } catch (error) {
        console.error('Error deleting comment:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Search public Cabaza profiles for participant selection
 * @param {string} query - Search query
 * @returns {Promise<Array>} List of public profiles
 */
export const searchParticipants = async (userId, query) => {
    try {
        const params = new URLSearchParams({ keyword: query, role: 'PUBLIC', limit: 10 });
        const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/peoples?${params}`, {
            headers: { 'Accept': 'application/json' }
        });
        if (!response.ok) throw new Error('Failed to search peoples');
        const data = await response.json();
        const success = data.success ?? data.SUCCESS;
        const peoples = data.data ?? data.DATA ?? [];
        return success ? peoples : [];
    } catch (error) {
        console.error('Error searching peoples:', error);
        return [];
    }
};

/**
 * Get status color
 * @param {string} status - Meeting status
 * @returns {string} Color hex
 */
export const getStatusColor = (status) => MEETING_STATUSES[status]?.color || '#6b7280';

/**
 * Get status label key for i18n
 * @param {string} status - Meeting status
 * @returns {string} i18n key (e.g., 'statusPending', 'statusConfirmed')
 */
export const getStatusLabelKey = (status) => {
    const keyMap = {
        pending: 'statusPending',
        confirmed: 'statusConfirmed',
        cancelled: 'statusCancelled',
        locked: 'statusLocked',
        proposal: 'statusProposal'
    };
    return keyMap[status] || status;
};

/**
 * Get type color
 * @param {string} type - Meeting type
 * @returns {string} Color hex
 */
export const getTypeColor = (type) => MEETING_TYPES[type]?.color || '#6b7280';

/**
 * Get type label key for i18n
 * @param {string} type - Meeting type
 * @returns {string} i18n key (e.g., 'typeInPerson', 'typeVideo')
 */
export const getTypeLabelKey = (type) => {
    const keyMap = {
        in_person: 'typeInPerson',
        video: 'typeVideo',
        phone: 'typePhone',
        other: 'typeOther'
    };
    return keyMap[type] || type;
};

/**
 * Format meeting date
 * @param {string} dateStr - Date string
 * @param {string} locale - Locale code
 * @returns {string} Formatted date
 */
export const formatMeetingDate = (dateStr, locale = 'fr-FR') => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString(locale, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
    });
};

/**
 * Format meeting time
 * @param {string} dateStr - Date string
 * @param {string} locale - Locale code
 * @returns {string} Formatted time
 */
export const formatMeetingTime = (dateStr, locale = 'fr-FR') => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleTimeString(locale, {
        hour: '2-digit',
        minute: '2-digit'
    });
};

/**
 * Format end time - shows only time if same day, otherwise full date + time
 * @param {string} startDateStr - Start date string
 * @param {string} endDateStr - End date string
 * @param {string} locale - Locale code
 * @returns {string} Formatted end time or empty string
 */
export const formatEndTime = (startDateStr, endDateStr, locale = 'fr-FR') => {
    if (!endDateStr) return '';
    const startDate = new Date(startDateStr);
    const endDate = new Date(endDateStr);

    // Check if same day
    const isSameDay = startDate.toDateString() === endDate.toDateString();

    if (isSameDay) {
        // Same day: show only time
        return endDate.toLocaleTimeString(locale, {
            hour: '2-digit',
            minute: '2-digit'
        });
    } else {
        // Different day: show date + time
        return endDate.toLocaleDateString(locale, {
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit'
        });
    }
};

/**
 * Get participant photo URL
 * @param {string} uuid - People UUID (not used anymore, kept for compatibility)
 * @param {string} filePath - File path from API (e.g., "peoples/photo.jpg")
 * @returns {string|null} Photo URL or null
 */
export const getParticipantPhotoUrl = (uuid, filePath) => {
    if (!filePath) return null;
    // If already a full URL, return as-is
    if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
        return filePath;
    }
    // Build full URL from filePath
    return `${FILES_BASE_URL}/${filePath.replace(/^\//, '')}`;
};

/**
 * Check if meeting is in the past
 * @param {string} dateStr - Meeting date string
 * @returns {boolean} True if past
 */
export const isMeetingPast = (dateStr) => {
    if (!dateStr) return false;
    return new Date(dateStr) < new Date();
};

/**
 * Check if meeting is today
 * @param {string} dateStr - Meeting date string
 * @returns {boolean} True if today
 */
export const isMeetingToday = (dateStr) => {
    if (!dateStr) return false;
    const meetingDate = new Date(dateStr);
    const today = new Date();
    return meetingDate.toDateString() === today.toDateString();
};

/**
 * Get time until meeting (returns raw data for i18n)
 * @param {string} dateStr - Meeting date string
 * @returns {Object|null} { type: 'past'|'now'|'minutes'|'hours'|'days', value?: number }
 */
export const getTimeUntilMeeting = (dateStr) => {
    if (!dateStr) return null;
    const now = new Date();
    const meetingDate = new Date(dateStr);
    const diff = meetingDate - now;

    if (diff < 0) return { type: 'past' };

    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return { type: 'days', value: days };
    if (hours > 0) return { type: 'hours', value: hours, minutes: minutes % 60 };
    if (minutes > 0) return { type: 'minutes', value: minutes };
    return { type: 'now' };
};
